import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2, Crown, Gamepad2, Github, Globe, Cpu, Sparkles, Layers, Trophy, Rocket, Joystick, Bug, Users, Cloud, PlayCircle } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import GameBlueprintCard from "../components/GameBlueprintCard";
import ReadAlongside from "../components/ReadAlongside";
import RepoToAppleDialog from "../components/RepoToAppleDialog";
import UrlToAppleDialog from "../components/UrlToAppleDialog";
import PushToGitHubDialog from "../components/PushToGitHubDialog";
import FileDropzone from "../components/FileDropzone";
import GameDebugDialog from "../components/GameDebugDialog";
import SceneEditorDialog from "../components/SceneEditorDialog";
import BrowserPreviewDialog from "../components/BrowserPreviewDialog";
import FollowUpPanel from "../components/FollowUpPanel";
import UpgradeNudge from "../components/UpgradeNudge";
import { toast } from "sonner";

const SAMPLES = [
  "A cozy match-3 puzzle where you align tea leaves to brew calming potions.",
  "An endless runner — a tiny saffron bird hops between cloud platforms collecting stars.",
  "A minimalist tower defense set inside a moving train carriage.",
  "A vision-pro spatial bowling game with hand-tracked throws.",
];

export default function GameStudio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [bp, setBp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [usage, setUsage] = useState(null);
  const [seedUploads, setSeedUploads] = useState([]);
  const [spriteUploads, setSpriteUploads] = useState([]);
  const [multiplayer, setMultiplayer] = useState(false);
  const [xcodeCloud, setXcodeCloud] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [repoOpen, setRepoOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [sceneOpen, setSceneOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bridgeHtml, setBridgeHtml] = useState(null);
  const [bridgeLoading, setBridgeLoading] = useState(false);

  useEffect(() => {
    if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("remix") === "1") {
      try {
        const stash = sessionStorage.getItem("dpa_remix_game");
        if (stash) {
          setBp(JSON.parse(stash));
          sessionStorage.removeItem("dpa_remix_game");
          toast.success("Remix loaded — tweak it and re-share!");
        }
      } catch { /* ignore */ }
    }
    const bpId = sp.get("blueprint_id");
    if (bpId && user) {
      api.get(`/games/mine/${bpId}`).then(({ data }) => {
        setBp(data);
        setSaved(true);
        toast.success(`Resumed: ${data.title}`);
      }).catch(() => toast.error("Could not load that game."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const generate = async (e) => {
    e?.preventDefault();
    if (prompt.trim().length < 10) return toast.error("Tell me a little more about your game idea.");
    setBusy(true); setBp(null); setSaved(false);
    try {
      const { data } = await api.post("/games/generate", {
        prompt,
        upload_ids: seedUploads.map(u => u.id),
        multiplayer,
      });
      setBp(data);
      toast.success(`Game blueprint ready — ${data.engine || "SpriteKit"} ✨`);
      if (user) api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {});
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "You've used all your prompts this month.");
      else toast.error(err.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!bp || !user) return user ? null : nav("/signup");
    setSaving(true);
    try {
      const { data } = await api.post("/games/save", bp);
      setBp(data);
      setSaved(true);
      toast.success("Saved to your dashboard");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const exportZip = async () => {
    if (!bp) return;
    try {
      const res = await fetch(`${API}/games/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("dpa_token") ? { Authorization: `Bearer ${localStorage.getItem("dpa_token")}` } : {}),
        },
        body: JSON.stringify({
          blueprint: bp,
          sprite_upload_ids: spriteUploads.map(u => u.id),
          xcode_cloud: xcodeCloud,
        }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(bp.title || "game").replace(/\s+/g, "")}-game-starter.zip`;
      a.click();
      toast.success("Game starter zip downloaded");
    } catch {
      toast.error("Could not export");
    }
  };

  const bridgeToBrowser = async () => {
    if (!bp) return;
    setBridgeHtml(null);
    setBridgeLoading(true);
    setPreviewOpen(true);
    try {
      const { data } = await api.post("/games/transpile", {
        blueprint: bp,
        sprite_upload_ids: spriteUploads.map(u => u.id),
      });
      setBridgeHtml(data.html);
      toast.success("Swift → Canvas bridge ready");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error(err.response?.data?.detail || "You're out of prompts this month.");
      else toast.error(err.response?.data?.detail || "Bridge failed. Try again.");
      setPreviewOpen(false);
    } finally {
      setBridgeLoading(false);
    }
  };

  const sharePreview = async () => {
    if (!bp) return;
    if (!user) {
      toast("Log in to share a public preview link.", {
        action: { label: "Log in", onClick: () => nav("/login") },
      });
      return;
    }
    try {
      const { data } = await api.post("/games/share-preview", bp);
      const url = `${window.location.origin}/preview/${data.id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Preview link copied", { description: url });
      } catch {
        toast.success("Preview ready", { description: url });
      }
      setBp({ ...bp, id: data.id, is_public: true, saved: true });
      setSaved(true);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not share preview");
    }
  };

  const branchBlueprint = () => {
    if (!bp) return;
    const cloned = {
      ...bp,
      id: undefined,
      saved: false,
      is_public: false,
      shared_at: null,
      branched_from: bp.id,
      title: bp.title?.endsWith("(branch)") ? bp.title : `${bp.title} (branch)`,
      refine_turns: [],
      created_at: new Date().toISOString(),
    };
    setBp(cloned);
    setSaved(false);
    toast.success("Branched — refine it without affecting the original");
  };

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="game-studio-page">
      <div className="text-center mb-8">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-2 justify-center">
          <Gamepad2 className="w-4 h-4" /> Apple Game Studio · beta
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight">
          From idea to <span className="italic text-marigold">playable</span>.
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          Describe a game in plain English. We'll pick the right Apple engine (SpriteKit, SceneKit, or RealityKit),
          design the gameplay loop, and hand back a real Xcode project with a SpriteKit scene that runs.
        </p>
      </div>

      {usage && (
        <div className="card-soft p-4 mb-4 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="game-usage">
          <span className="text-ink2">
            {usage.unlimited ? <span className="text-marigold font-medium">Unlimited prompts ✨</span> : <>Monthly prompts: <span className="font-semibold text-ink">{usage.used}</span> / {usage.limit}</>}
          </span>
          {!usage.unlimited && usage.remaining <= 1 && (
            <a href="/pricing" className="inline-flex items-center gap-1 text-marigold font-medium hover:underline">
              <Crown className="w-3.5 h-3.5" /> Upgrade for more
            </a>
          )}
        </div>
      )}

      <UpgradeNudge usage={usage} variant="game" />

      <form onSubmit={generate} className="card-soft p-5 md:p-6 mb-4" data-testid="game-form">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder={SAMPLES[0]}
          className="w-full bg-transparent text-ink text-lg placeholder:text-ink2/60 outline-none resize-none"
          data-testid="game-prompt-textarea"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
          <div className="text-xs text-ink2">{prompt.length} / 2000 chars · powered by Claude Sonnet 4.5</div>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="game-generate-button">
            <Wand2 className="w-4 h-4" /> {busy ? "Designing…" : "Generate game blueprint"}
          </button>
        </div>
      </form>

      {/* Read alongside — chapters from the curriculum that pair with a game brief */}
      <div className="mb-4">
        <ReadAlongside
          seeds={prompt || "game loop state machine input timing spritekit scenekit realitykit"}
          eyebrow="Read alongside as you build"
          title="chapters from the curriculum for this game"
          askPrompt={prompt ? `As I prompt "${prompt.slice(0, 200)}" in Game Studio, what curriculum ideas will help me most?` : `What chapters from the curriculum help me build an Apple game in Game Studio?`}
          defaultOpen={false}
          testId="game-read-alongside"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="game-samples">
        <span className="text-xs text-ink2 self-center mr-1">Try:</span>
        {SAMPLES.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setPrompt(s)}
            className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-ink hover:bg-ink hover:text-cream transition"
            data-testid={`game-sample-${i}`}
          >
            {s.length > 70 ? s.slice(0, 70) + "…" : s}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <FileDropzone
          purpose="seed"
          value={seedUploads}
          onChange={setSeedUploads}
          label="Reference sketches, mood boards, or design notes (optional)"
          hint="PNG/JPG/PDF/MD — the AI looks at these when designing your game"
        />
        <FileDropzone
          purpose="asset"
          value={spriteUploads}
          onChange={setSpriteUploads}
          label="Sprites for your game (player, enemies, pickups)"
          hint="PNG/JPG/WEBP — bundled into Assets.xcassets/Sprites/ + a type-safe Sprite enum"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <label className="card-soft p-4 cursor-pointer hover:border-marigold transition flex items-start gap-3" data-testid="game-multiplayer-toggle">
          <input
            type="checkbox"
            checked={multiplayer}
            onChange={(e) => setMultiplayer(e.target.checked)}
            className="mt-1 accent-marigold w-4 h-4 flex-shrink-0"
          />
          <div className="flex-1">
            <div className="font-semibold text-ink inline-flex items-center gap-1.5">
              <Users className="w-4 h-4 text-marigold" /> Multiplayer / GameKit
            </div>
            <div className="text-xs text-ink2 mt-0.5">
              Bundles `GameKitService`, `MultiplayerService` (real-time), and `TurnBasedService` Swift modules + a setup `GAMEKIT.md`. Toggle this BEFORE generating so the AI also designs the multiplayer mode into the gameplay loop.
            </div>
          </div>
        </label>
        <label className="card-soft p-4 cursor-pointer hover:border-marigold transition flex items-start gap-3" data-testid="game-xcode-cloud-toggle">
          <input
            type="checkbox"
            checked={xcodeCloud}
            onChange={(e) => setXcodeCloud(e.target.checked)}
            className="mt-1 accent-marigold w-4 h-4 flex-shrink-0"
          />
          <div className="flex-1">
            <div className="font-semibold text-ink inline-flex items-center gap-1.5">
              <Cloud className="w-4 h-4 text-marigold" /> Open in Xcode Cloud
            </div>
            <div className="text-xs text-ink2 mt-0.5">
              Adds `ci_scripts/ci_post_clone.sh` + `XCODE_CLOUD.md` to your zip. Open in Xcode 15+ → Product → Xcode Cloud → Create Workflow → Universal Build for iOS + macOS + visionOS in one tag.
            </div>
          </div>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-8 text-sm">
        <span className="text-ink2">Or start from</span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setDebugOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
            data-testid="game-debug-button"
          >
            <Bug className="w-4 h-4" /> Debug a Swift error
          </button>
          <button
            type="button"
            onClick={() => setUrlOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
            data-testid="game-import-url-button"
          >
            <Globe className="w-4 h-4" /> URL → Apple PWA
          </button>
          <button
            type="button"
            onClick={() => setRepoOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition"
            data-testid="game-import-repo-button"
          >
            <Github className="w-4 h-4" /> Import a GitHub repo
          </button>
        </div>
      </div>

      {bp && (
        <GameBlueprintCard
          bp={bp}
          onSave={save}
          saving={saving}
          saved={saved}
          onExport={exportZip}
          onPushGitHub={() => setPushOpen(true)}
          onPreview={() => { setBridgeHtml(null); setPreviewOpen(true); }}
          onBridge={bridgeToBrowser}
          onShare={sharePreview}
          onBranch={branchBlueprint}
        />
      )}

      {bp && (
        <div className="mt-6">
          <FollowUpPanel
            blueprint={bp}
            endpoint="/games/refine"
            variant="game"
            onUpdate={(newBp) => { setBp(newBp); setSaved(false); }}
            onUsageRefresh={() => user && api.get("/prompt/usage").then(({ data }) => setUsage(data)).catch(() => {})}
          />
        </div>
      )}

      {!bp && !busy && (
        <FeatureGrid />
      )}

      <RepoToAppleDialog open={repoOpen} onOpenChange={setRepoOpen} onBlueprint={() => { toast.message("Game-mode repo conversion coming soon — saved as an app blueprint for now."); nav("/studio"); }} />
      <UrlToAppleDialog open={urlOpen} onOpenChange={setUrlOpen} onBlueprint={() => { toast.message("URL conversion saved as an app blueprint."); nav("/studio"); }} />
      <PushToGitHubDialog open={pushOpen} onOpenChange={setPushOpen} blueprint={bp ? { ...bp, files: undefined } : null} platforms={["ios"]} />
      <GameDebugDialog open={debugOpen} onOpenChange={setDebugOpen} defaultEngine={bp?.engine || "SpriteKit"} />
      <BrowserPreviewDialog
        open={previewOpen}
        onOpenChange={(v) => { setPreviewOpen(v); if (!v) { setBridgeHtml(null); setBridgeLoading(false); } }}
        blueprint={bp}
        spriteUploads={spriteUploads}
        overrideHtml={bridgeHtml}
        mode={bridgeHtml || bridgeLoading ? "bridge" : "procedural"}
        loading={bridgeLoading}
      />
      <SceneEditorDialog
        open={sceneOpen}
        onOpenChange={setSceneOpen}
        blueprint={bp}
        onApply={({ engine, scene, code, filename }) => {
          // Stash scene patches on the blueprint so the export bundles them
          const patches = (bp?.scene_patches || []).filter(p => p.path !== `Game/${filename}`);
          patches.push({ path: `Game/${filename}`, content: code });
          setBp({ ...bp, engine, scene_patches: patches, starter_code: code });
        }}
      />

      {busy && (
        <div className="fixed left-0 right-0 bottom-6 mx-auto w-fit px-5 py-3 rounded-full bg-ink text-cream text-sm shadow-2xl shadow-ink/30 inline-flex items-center gap-2 animate-pulse" data-testid="game-busy-toast">
          <Cpu className="w-4 h-4" /> Designing your game systems…
        </div>
      )}
    </div>
  );
}

function FeatureGrid() {
  const items = [
    { icon: Sparkles, title: "Engine picked for you", body: "SpriteKit for 2D, SceneKit for 3D, RealityKit for AR/Vision Pro. Auto-decided from your prompt." },
    { icon: Layers, title: "Real Xcode project", body: "Full project tree — App / Game / Systems / Entities / UI — with a SKScene that compiles and runs." },
    { icon: Joystick, title: "Gameplay loop & mechanics", body: "Start → play → challenge → reward → unlock. Mechanics, scoring, and 3-5 levels designed for you." },
    { icon: Trophy, title: "Game Center ready", body: "Generated lesson path covers leaderboards, achievements, and TestFlight beta in 45 minutes." },
    { icon: Github, title: "Push & ship", body: "One click to push to GitHub. The bundled Actions workflow uploads to TestFlight on every tag." },
    { icon: Rocket, title: "From prompt to App Store", body: "Same path as the App Studio — but tuned for the way games actually feel." },
  ];
  return (
    <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="game-feature-grid">
      {items.map((it) => (
        <div key={it.title} className="card-soft p-5">
          <div className="w-10 h-10 rounded-xl bg-marigold/15 text-marigold flex items-center justify-center mb-3">
            <it.icon className="w-5 h-5" />
          </div>
          <h3 className="font-heading text-lg font-semibold mb-1">{it.title}</h3>
          <p className="text-sm text-ink2 leading-relaxed">{it.body}</p>
        </div>
      ))}
    </section>
  );
}
